<template>
  <div>
    <TopSection
      :title="fileName"
      :count="count"
      :section="$t('uploads')"
      :sub-section="$t('landed_sound_recordings')"
    >
      <template v-slot:actions>
        <div class="mr-3">
          <span
              v-b-tooltip.hover
              class="link-class"
              :title="$t('download')"
              @click="downloadRepertoireFile()"
          >
            <i class="far fa-download" />
            {{ $t('downloadFile') | capitalize }}
          </span>
        </div>
        <div class="mr-3">
          <Status
            :count="fileStates[statusTypes.CREATED] || 0"
            :text="$t('created') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            :count="fileStates[statusTypes.LINKED] || 0"
            :text="$t('linked') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            :count="fileStates[statusTypes.MANUALLY_CREATED] || 0"
            :text="$t('manually_created') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
        </div>
        <div class="mr-3">
          <Status
            :count="fileStates[statusTypes.MANUALLY_LINKED] || 0"
            :text="$t('manually_linked') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            :count="fileStates[statusTypes.PENDING_FOR_VALIDATION] || 0"
            :text="$t('pending_for_validation') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
          />
        </div>
      </template>

      <template v-slot:filters>
        <FilterModule
          :title="$t('filterSoundRecordings') | capitalize"
          :statuses="{
            [statusTypes.CREATED]: $t('created'),
            [statusTypes.LINKED]: $t('linked'),
            [statusTypes.MANUALLY_CREATED]: $t('manually_created'),
            [statusTypes.MANUALLY_LINKED]: $t('manually_linked'),
            [statusTypes.PENDING_FOR_VALIDATION]: $t('pending_for_validation')
          }"
          :table="$refs.LandingSRs"
        />
      </template>
      
      <template v-slot:title__after>
        <SelectedFilters class="ml-3" :table="$refs.LandingSRs" />
      </template>
    </TopSection>

    <!-- TABLE -->
    <v-server-table
      v-if="dataReady"
      ref="LandingSRs"
      :url="`/landingsoundrecordings/?file_id=${this.$route.params.id}`"
      :columns="soundrecordingFilesColumns"
      :options="options"
      @row-click="selectRow"
      @pagination="onPagination"
      @sorted="onSorted"
      @filter="onFilter"
    >
      <!-- TITLE COLUMN -->
      <template v-slot:title="{ row }">
        <div class="table-header">{{ row.title | capitalize }}</div>
      </template>

      <!-- ARTIST COLUMN -->
      <template v-slot:artist="{ row }">
        <div class="table-header">{{ row.artist | capitalize }}</div>
      </template>

      <!-- ISRC COLUMN -->
      <template v-slot:isrc="{ row }">
        <span class="medium">{{ row.isrc | upper }}</span>
      </template>

      <!-- DURATION COLUMN-->
      <template v-slot:duration="{ row }">
        {{ row.duration | normalizeSeconds }}
      </template>

      <!--TYPE COLUMN-->
      <template v-slot:type="{ row }">
        <span>{{ row.type | getTypeName(srTypes) }}</span>
      </template>

      <!-- COUNTRY OF RECORDING COLUMN-->
      <template v-slot:fixation_country="{ row }">
        {{ row.fixation_country_name }}
      </template>

      <!-- LANGUAGE OF PERF COLUMN-->
      <template v-slot:lang_of_perf="{ row }">
        <div
          v-for="lang_of_perf in row.lang_of_perf"
          :key="lang_of_perf"
          class="table-meta mb-2"
        >
          {{ lang_of_perf }}
        </div>
      </template>

      <!-- RELEASE NAMES COLUMN-->
      <template v-slot:releaseNames="{ row }">
        <ReleaseNames
          class-names="table-meta"
          :releases="row.release_names"
        />
      </template>

      <!-- STATUS COLUMN-->
      <template v-slot:status="{ row }">
        <Status
          v-if="row.status == statusTypes.CREATED"
          :text="$t('created') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.LINKED"
          :text="$t('linked') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.MANUALLY_CREATED"
          :text="$t('manually_created') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.MANUALLY_LINKED"
          :text="$t('manually_linked') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.PENDING_FOR_VALIDATION"
          :text="$t('pending_for_validation') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
      </template>

      <!-- ACTIONS COLUMN -->
      <template v-slot:actions="{ row }">
        <div class="d-flex justify-content-end">
          <span
            v-if="isPending(row)"
            class="clickable table-sub-class text-sm pr-4"
          >
            <i v-if="organization===row.source || !row.is_sister_society_upload"
              :class="selectedSoundRecording.id===row.id ? 'fa fa-chevron-up':'fa fa-pencil'"
            />
          </span>
          <span
            v-else
            class="clickable table-sub-class text-sm pr-4"
            @click="navigateToSR(row.sound_recording)"
          >
            <i class="fas fa-eye" />
          </span>
          <span
            class="clickable table-sub-class text-sm pr-4"
            v-if="organization===row.source || !row.is_sister_society_upload"
            @click="invalidate(row)"
          >
            <i class="icon fa fa-times red text-center"/>
          </span>
        </div>
      </template>

      <!-- CHILD ROWS -->
      <template v-slot:child_row="props">
        <div :key="props.row.id" class="d-flex justify-content-between py-4 pl-2">
          <div class="medium-big">
            {{ $t('proposedCandidates') | capitalize }}
          </div>
          <div class="d-flex">
            <button
              v-b-modal="'recordingDetailsModal'"
              class="button"
            >
              <i class="table-sub-class text-sm far fa-music mr-2" />
              {{ $t('createNewSoundRecording') | capitalize }}
            </button>

            <div class="ml-4 button">
              <i class="table-sub-class text-sm far fa-search mr-2" />
              <input
                v-model="props.row.search_text"
                class="search"
                placeholder="Search Candidates"
              >
            </div>
          </div>
        </div>
        <SelectRecordTable
          :id="props.row.id"
          :search_text="props.row.search_text"
          @selectedSuccess="recordSelected()"
          @closing="props.row.search_text=''"
        />
      </template>
    </v-server-table>

    <RecordingDetailsModal
      :key="selectedSoundRecording.id"
      ref="recordingDetailsModal"
      :sound-recording="selectedSoundRecording"
      @close="closeModal"
    />

      <!-- Deletion Confirmation -->
    <b-modal
      id="confirmDeletion"
      ref="confirmDeletion"
      :title="$t('invalidateSource') | capitalize"
      hide-footer
    >
      <p>{{ $t("invalidateSourceInfo") }}</p>
      <div class="flex justify-end">
        <action-button
          variant="primary"
          size="big"
          @click="confirmInvalidation()"
        >
          {{ $t("invalidateSource") | capitalize }}
        </action-button>
      </div>
    </b-modal>

    <!-- The target of the link is the iframe, given by its name -->
    <div style="display:none;">
      <iframe
        v-for="link in downloadLinks"
        :key="link"
        :name="link"
      >
        <a :target="link" :href="link" :id="link" download>{{ link }}</a>
      </iframe>
    </div>
  </div>
</template>



<style lang="scss" scoped>
input.search {
  border: 0 !important;
  background: transparent;
  width: 80%;
}

textarea:focus,
input:focus {
  outline: none;
}

.blurred {
  filter: blur(2px);
}

</style>


<script>
import { capitalize, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapActions, mapGetters, mapState } from "vuex"

import { ApiCalls } from "@/assets/libs/services"
import RecordingDetailsModal from "../../Modules/RecordingDetailsModal.vue"
import { api } from "@/main"
import { listRouteMixin } from "@/utils/mixins"


export default {
  name: "LandingSRs",
  mixins: [listRouteMixin],
  components: {
    RecordingDetailsModal
  },

  created () {
    this.$store.commit("ui/setStatusFilter", "")
  },
  methods: {
    ...mapActions("ui", ["clearFilters"]),
    addRecordingDetails () {
      this.recordingDetailsModalOpen = true
    },
    closeModal (modal) {
      const { LandingSRs } = this.$refs
      this.$refs.LandingSRs.toggleChildRow(
        this.selectedSoundRecording.id
      )
      this.selectedSoundRecording = {}

      LandingSRs.refresh()
      this.$nextTick(() => {
        // Wrapped in $nextTick to ensure DOM is rendered before closing
        modal.hide()
      })
    },
    clickOnDownLoadLink (downloadLink) {
      this.downloadLinks = [downloadLink]
      this.$nextTick(() => {
        const link = document.getElementById(downloadLink)
        link.click()
      })
    },
    downloadRepertoireFile () {
      api
        .get(`repertoirefiles/${this.$route.params.id}/download/`)
        .then(res => {
          const downloadLink = res.data
          try {
            this.clickOnDownLoadLink(downloadLink)
          } catch (error) {
            this.$toasted.global.toast(error)
          }
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.showErrors(err.response.data)
          } else {
            this.$toasted.global.toast(err)
          }
        })
    },
    getData () {
      this.$refs.LandingSRs.getData()
    },
    getMetadata (row, tag) {
      const extras = this.$options.filters.get(
        row,
        "sound_recording.extras",
        null
      )
      if (extras) {
        return extras.reduce((current, next) => {
          if (next.tag === tag) {
            current.push(next.value)
          }
          return current
        }, [])
      }
      return []
    },
    getStatsDisplay (key) {
      return {
        created_srs: this.$t("created"),
        linked_srs: this.$t("linked"),
        manually_created_srs: this.$t("manually_created"),
        manually_linked_srs: this.$t("manually_linked"),
        pending_for_validation: this.$t("pending_for_validation"),
      }[key]
    },
    navigateToSR ({ id }) {
      this.$router.push({
        name: "sound-recording-from-landed-detail",
        params: {
          id
        }
      })
    },
    onFilter (search) {
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
    isPending (row) {
      return row.status === this.statusTypes.PENDING_FOR_VALIDATION
    },
    recordSelected () {
      const { LandingSRs } = this.$refs
      LandingSRs.toggleChildRow(this.selectedSoundRecording.id)
      this.selectedSoundRecording = {}

      LandingSRs.refresh()
    },
    showErrors (errors) {
      errors.forEach(msg => { this.$toasted.global.toast(msg) })
    },
    goBack () {
      this.$router.push({ name: "sound-recordings" })
    },
    selectRow ({ row }) {
      const { status } = row
      const disabled = this.organization!==row.source && row.is_sister_society_upload
      if (status !== this.statusTypes.PENDING_FOR_VALIDATION || disabled) {
        return
      }
      const { LandingSRs } = this.$refs

      if (this.selectedSoundRecording.id === row.id) {
        LandingSRs.toggleChildRow(row.id)
        this.selectedSoundRecording = {}
      } else {
        LandingSRs.toggleChildRow(this.selectedSoundRecording.id)
        LandingSRs.toggleChildRow(row.id)
        this.selectedSoundRecording = row
      }
    },
    confirmInvalidation () {
      this.loading = true
      return ApiCalls.invalidate(this.selectedSource).then(()=>{
        this.$toasted.global.toast(this.$t("invalidatedSource"))
      })
        .catch(err => {
          this.$toasted.global.toast(err.response.data.detail)
        }).finally(() => {
          this.loading = false
          this.$bvModal.hide("confirmDeletion")
          this.$refs.LandingSRs.refresh()
        })
    },
    invalidate ( row ) {
      this.selectedSource = row.id
      this.$bvModal.show("confirmDeletion")
    },
  },
  computed: {
    ...mapState("soundsys", ["dataReady"]),
    ...mapState("ui", ["statusFilter"]),
    ...mapGetters("soundsys", ["srTypes", "statusTypes", "organization"]),
  },
  beforeRouteLeave (to, from, next) {
    // clear filters when change route
    this.clearFilters()

    next()
  },

  data () {
    return {
      selectedSoundRecording: {},
      selectedSource: null,
      recordingDetailsModalOpen: false,
      downloadLinks: [],
      fileStates: {},
      fileName: "",
      created: "",
      count: 0,
      soundrecordingFilesColumns: [
        "title",
        "artist",
        "isrc",
        "duration",
        "type",
        "fixation_country",
        "lang_of_perf",
        "fixation_year",
        "releaseNames",
        "status",
        "actions"
      ],

      options: {
        rowClassCallback: row => {
          // If an item is selected
          if (Object.keys(this.selectedSoundRecording).length) {
            if (row.id !== this.selectedSoundRecording.id) {
              return "blurred"
            }
            if (row.id === this.selectedSoundRecording.id) {
              return "clickable mh70"
            }
          }

          return "clickable mh70"
        },
        sortable: ["title", "isrc", "artist", "status"],
        filterable: true,
        headings: {
          title: capitalize(this.$t("title")),
          artist: capitalize(this.$t("artist")),
          isrc: capitalize(this.$t("isrc")),
          duration: capitalize(this.$t("duration")),
          type: capitalize(this.$t("type")),
          fixation_country: capitalize(this.$t("fixation_country")),
          lang_of_perf: capitalize(this.$t("lang_of_perf")),
          fixation_year: capitalize(this.$t("fixation_year")),
          releaseNames: capitalize(this.$t("releaseNames")),
          status: capitalize(this.$t("status")),
          actions: "",
        },
        columnsClasses: {
          title: "width-medium",
          artist: "width-medium",
          isrc: "width-medium",
          duration: "width-small",
          type: "width-small",
          fixation_country: "width-small",
          fixation_year: "width-small",
          releaseNames: "width-medium",
          lang_of_perf: "width-small",
          status: "width-medium",
          actions: "width-small"
        },
        requestFunction (params) {
          const query = { ...params, ...this.$route.query }
          this.query = get(query, "fuzzy_search", "")
          this.page = Number(get(query, "page", 1))
          return doRequest(this.url, query)
        },
        responseAdapter: resp => {
          if (this.selectedSoundRecording.id) {
            this.$refs.LandingSRs.toggleChildRow(
              this.selectedSoundRecording.id
            )
            this.selectedSoundRecording = {}
          }
          if (resp && resp.data) {
            this.fileStates = resp.data.states
            this.fileName = resp.data.fileName.split("/").pop()
            this.created = resp.data.created
            this.count = Number(resp.data.count)
          }
          return responsePaginatedAdapter(resp)
        }
      }
    }
  }
}
</script>
